import React, { useState, useRef, useEffect } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import { Button } from 'antd';
import { FullscreenOutlined, FullscreenExitOutlined } from '@ant-design/icons';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';

const BrowserFullscreenCalendar = (props) => {
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [currentView, setCurrentView] = useState('week');
    const [currentDate, setCurrentDate] = useState(new Date());
    const calendarRef = useRef(null);
    const updateTimeoutRef = useRef(null);
    const scrollTimeoutRef = useRef(null);
    
    // Set Romanian locale
    moment.locale('ro');
    const localizer = momentLocalizer(moment);
    
    // Define custom formats for the calendar
    const formats = {
        eventTimeRangeFormat: () => '',
        eventTimeRangeEndFormat: () => '',
        // Week view header format: "28 Aprilie - 4 Mai"
        dayRangeHeaderFormat: ({ start, end }, culture, localizer) => {
            // Capitalize month names
            const startMonth = localizer.format(start, 'MMMM', culture);
            const endMonth = localizer.format(end, 'MMMM', culture);
            
            const capitalizedStartMonth = startMonth.charAt(0).toUpperCase() + startMonth.slice(1);
            const capitalizedEndMonth = endMonth.charAt(0).toUpperCase() + endMonth.slice(1);
            
            // Format as "DD Month - DD Month"
            return `${localizer.format(start, 'D', culture)} ${capitalizedStartMonth} - ${localizer.format(end, 'D', culture)} ${capitalizedEndMonth}`;
        },
        
        // Day view header format: "Marți, 29 Aprilie"
        dayHeaderFormat: (date, culture, localizer) => {
            // Get day name and capitalize it
            const dayName = localizer.format(date, 'dddd', culture);
            const capitalizedDayName = dayName.charAt(0).toUpperCase() + dayName.slice(1);
            
            // Get month name and capitalize it
            const monthName = localizer.format(date, 'MMMM', culture);
            const capitalizedMonth = monthName.charAt(0).toUpperCase() + monthName.slice(1);
            
            // Format as "Day, DD Month"
            return `${capitalizedDayName}, ${localizer.format(date, 'D', culture)} ${capitalizedMonth}`;
        },
        
        // Other formats you might want to customize
        dayFormat: (date, culture, localizer) => {
            const dayName = localizer.format(date, 'ddd', culture);
            return localizer.format(date, 'DD ', culture) + dayName.charAt(0).toUpperCase() + dayName.slice(1);
        },
        
        monthHeaderFormat: (date, culture, localizer) => {
            const monthName = localizer.format(date, 'MMMM', culture);
            return monthName.charAt(0).toUpperCase() + monthName.slice(1) + ' ' + localizer.format(date, 'YYYY', culture);
        }
    };

    // Set up fullscreen change event listener
    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(
                document.fullscreenElement ||
                document.webkitFullscreenElement ||
                document.mozFullScreenElement ||
                document.msFullscreenElement
            );
        };

        document.addEventListener('fullscreenchange', handleFullscreenChange);
        document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
        document.addEventListener('mozfullscreenchange', handleFullscreenChange);
        document.addEventListener('MSFullscreenChange', handleFullscreenChange);

        return () => {
            document.removeEventListener('fullscreenchange', handleFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
            document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
            document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
        };
    }, []);

    // Function to scroll to 09:00
    const scrollToNineAM = () => {
        if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
        }

        scrollTimeoutRef.current = setTimeout(() => {
            const calendarElement = calendarRef.current;
            if (!calendarElement) return;

            // Find the scrollable content area
            const scrollContainer = calendarElement.querySelector('.rbc-time-content') || 
                                   calendarElement.querySelector('.rbc-time-view');
            
            if (!scrollContainer) return;

            // Calculate scroll position for 09:00
            // Each hour typically takes about 60px in the default calendar view
            // 9 hours * 60px = 540px (adjust if needed based on your styling)
            const hourHeight = 60; // You might need to adjust this value
            const targetHour = 9;
            const scrollPosition = targetHour * hourHeight;

            // Smooth scroll to the position
            scrollContainer.scrollTo({
                top: scrollPosition,
                behavior: 'smooth'
            });

            scrollTimeoutRef.current = null;
        }, 100); // Small delay to ensure calendar is fully rendered
    };

    // Function to calculate hourly capacity - Excludes end hour
    const calculateHourlyCapacity = () => {
        if (!props.bookings || props.bookings.length === 0) {
            return {};
        }

        const capacityByDateAndHour = {};

        // Process each booking
        props.bookings.forEach(booking => {
            // Skip cancelled bookings
            if (booking.status === 'cancelled') return;

            // Get date from booking
            const dateStr = booking.reservationDate ?
                (booking.reservationDate.includes('T') ?
                    booking.reservationDate.split('T')[0] :
                    booking.reservationDate) : '';

            if (!dateStr) return;

            // Get start and end times
            const startTimeStr = booking.hourStart ?
                (booking.hourStart.includes(':') ?
                    booking.hourStart.split(':').slice(0, 2).join(':') :
                    booking.hourStart) : '';
            const endTimeStr = booking.hourEnd ?
                (booking.hourEnd.includes(':') ?
                    booking.hourEnd.split(':').slice(0, 2).join(':') :
                    booking.hourEnd) : '';

            if (!startTimeStr || !endTimeStr) return;

            // Parse start and end hours and minutes
            const [startHour, startMinute] = startTimeStr.split(':').map(Number);
            const [endHour, endMinute] = endTimeStr.split(':').map(Number);

            // Initialize the date in the capacity object if not exists
            if (!capacityByDateAndHour[dateStr]) {
                capacityByDateAndHour[dateStr] = {};
            }

            // Add party size to each hour the booking spans
            // Only include hours that are fully or partially within the booking time
            // Do not include the exact end hour (only hours before it)
            for (let hour = startHour; hour < endHour; hour++) {
                const hourKey = `${hour}:00`;

                if (!capacityByDateAndHour[dateStr][hourKey]) {
                    capacityByDateAndHour[dateStr][hourKey] = 0;
                }

                capacityByDateAndHour[dateStr][hourKey] += booking.partySize;
            }

            // Special case: if booking is within the same hour
            if (startHour === endHour) {
                const hourKey = `${startHour}:00`;

                if (!capacityByDateAndHour[dateStr][hourKey]) {
                    capacityByDateAndHour[dateStr][hourKey] = 0;
                }

                capacityByDateAndHour[dateStr][hourKey] += booking.partySize;
            }
        });

        return capacityByDateAndHour;
    };

    // Function to enter fullscreen
    const enterFullscreen = () => {
        const element = calendarRef.current;
        if (!element) return;

        if (element.requestFullscreen) {
            element.requestFullscreen();
        } else if (element.webkitRequestFullscreen) { /* Safari */
            element.webkitRequestFullscreen();
        } else if (element.msRequestFullscreen) { /* IE11 */
            element.msRequestFullscreen();
        }
    };

    // Function to exit fullscreen
    const exitFullscreen = () => {
        if (document.exitFullscreen) {
            document.exitFullscreen();
        } else if (document.webkitExitFullscreen) { /* Safari */
            document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) { /* IE11 */
            document.msExitFullscreen();
        }
    };

    // Toggle fullscreen
    const toggleFullscreen = () => {
        if (isFullscreen) {
            exitFullscreen();
        } else {
            enterFullscreen();
        }
    };

    // Handle view changes
    const handleViewChange = (view) => {
        setCurrentView(view);

        // Clear capacity indicators when not in day view
        if (view !== 'day') {
            document.querySelectorAll('.capacity-indicator').forEach(el => el.remove());
        } else {
            // Update indicators when switching to day view
            if (updateTimeoutRef.current) {
                clearTimeout(updateTimeoutRef.current);
            }
            updateTimeoutRef.current = setTimeout(() => {
                updateCapacityIndicators();
                updateTimeoutRef.current = null;
            }, 100);
        }

        // Scroll to 9 AM for day and week views
        if (view === 'day' || view === 'week') {
            scrollToNineAM();
        }
    };

    // Handle date changes (next/prev/today navigation)
    const handleDateChange = (date) => {
        setCurrentDate(date);
        
        // Clear any pending update
        if (updateTimeoutRef.current) {
            clearTimeout(updateTimeoutRef.current);
        }
        
        // Schedule a new update with the new date
        updateTimeoutRef.current = setTimeout(() => {
            updateCapacityIndicators();
            updateTimeoutRef.current = null;
        }, 100);

        // Scroll to 9 AM for day and week views
        if (currentView === 'day' || currentView === 'week') {
            scrollToNineAM();
        }
    };

    // Custom event handler that exits fullscreen before calling the original handler
    const handleEventClick = (event) => {
        // If you want to exit fullscreen when an event is clicked
        if (isFullscreen) {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if (document.webkitExitFullscreen) {
                document.webkitExitFullscreen();
            } else if (document.msExitFullscreen) {
                document.msExitFullscreen();
            }
        }

        // Then call the original onSelectEvent handler if it exists
        if (props.onSelectEvent) {
            props.onSelectEvent(event);
        }
    };

    // Update capacity indicators whenever view, date, or bookings change
    useEffect(() => {
        if (!calendarRef.current || currentView !== 'day') {
            // Clear indicators if not in day view
            document.querySelectorAll('.capacity-indicator').forEach(el => el.remove());
            return;
        }

        // Clear any pending update
        if (updateTimeoutRef.current) {
            clearTimeout(updateTimeoutRef.current);
        }
        
        // Schedule a new update
        updateTimeoutRef.current = setTimeout(() => {
            updateCapacityIndicators();
            updateTimeoutRef.current = null;
        }, 300);

        return () => {
            if (updateTimeoutRef.current) {
                clearTimeout(updateTimeoutRef.current);
                updateTimeoutRef.current = null;
            }
        };
    }, [currentView, currentDate, props.bookings]);

    // Auto-scroll to 9 AM on initial load and view/date changes
    useEffect(() => {
        if (currentView === 'day' || currentView === 'week') {
            scrollToNineAM();
        }

        return () => {
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
                scrollTimeoutRef.current = null;
            }
        };
    }, [currentView, currentDate, isFullscreen]);

    // Function to update capacity indicators - doesn't depend on component state
    const updateCapacityIndicators = () => {
        // Only update if in day view
        if (currentView !== 'day') return;

        // First, remove any existing capacity indicators
        document.querySelectorAll('.capacity-indicator').forEach(el => el.remove());

        const capacityData = calculateHourlyCapacity();
        if (Object.keys(capacityData).length === 0) return;

        // Get current displayed date in YYYY-MM-DD format
        const selectedDate = moment(currentDate).format('YYYY-MM-DD');
        
        // Log to help with debugging
        // console.log('Updating capacity indicators for date:', selectedDate);
        // console.log('Available capacity data dates:', Object.keys(capacityData));
        
        // Get all time labels in the left gutter
        const timeLabels = document.querySelectorAll('.rbc-time-gutter .rbc-label');

        timeLabels.forEach(label => {
            // Get the time text (e.g., "9:00" or "14:00")
            const timeText = label.textContent;

            // Extract hour from format like "14:00" or "2:00"
            const hourMatch = timeText.match(/(\d+):00/);

            if (hourMatch) {
                const hour = parseInt(hourMatch[1], 10);
                const hourKey = `${hour}:00`;

                if (capacityData[selectedDate] && capacityData[selectedDate][hourKey]) {
                    const capacity = capacityData[selectedDate][hourKey];
                    addCapacityIndicator(label, capacity);
                }
            }
        });
    };

    // Helper function to add capacity indicator to a DOM element
    const addCapacityIndicator = (element, capacity) => {
        // Check if an indicator already exists
        if (element.querySelector('.capacity-indicator')) return;

        // Create capacity indicator
        const indicator = document.createElement('span');
        indicator.className = 'capacity-indicator';
        indicator.textContent = capacity.toString();

        // Style based on capacity level
        indicator.style.cssText = `
            display: inline-block;
            margin-left: 8px;
            font-size: 12px;
            font-weight: bold;
            padding: 0px 5px;
            border-radius: 3px;
            background-color: rgba(76, 175, 80, 0.2);
            color: #2e7d32;
        `;

        // Set color based on capacity level
        if (capacity > 20) {
            indicator.style.backgroundColor = 'rgba(244, 67, 54, 0.2)';
            indicator.style.color = '#b71c1c';
        } else if (capacity > 10) {
            indicator.style.backgroundColor = 'rgba(255, 152, 0, 0.2)';
            indicator.style.color = '#e65100';
        }

        // Add tooltip
        indicator.title = `Capacitate ocupata: ${capacity}`;

        // Append to element
        element.appendChild(indicator);
    };

    return (
        <div className="calendar-container" ref={calendarRef}>
            <div className="calendar-header">
                <Button
                    type="secondary"
                    icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
                    onClick={toggleFullscreen}
                    className="fullscreen-button"
                    style={{ display: 'none' }}
                >
                    {isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                </Button>
            </div>

            <div className={`calendar-wrapper ${isFullscreen ? 'fullscreen-active' : ''}`}>
                <Calendar
                    localizer={localizer}
                    style={{ height: isFullscreen ? 'calc(100vh - 50px)' : 600 }}
                    onSelectEvent={handleEventClick}
                    onView={handleViewChange}
                    onNavigate={handleDateChange}
                    date={currentDate}
                    formats={formats} 
                    {...props} // Pass all props directly to the Calendar component
                />
            </div>
        </div>
    );
};

export default BrowserFullscreenCalendar;